// NEW_FILE_JUSTIFIED: AI-extracted-facts panel for the lead detail page — no existing component
// surfaces leadFacts grouped by category.
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official client-side React bindings package, not a server module
import { formatDistanceToNow } from 'date-fns';
import { Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { api } from '@convex/_generated/api';
import type { ILeadFactsPanelProps, TLeadFact, TLeadFactCategory } from '@/@types/leadPanels';

const CATEGORY_ORDER: TLeadFactCategory[] = [
  'contact',
  'business',
  'coverage',
  'decision',
  'next_step',
  'other',
];

const CATEGORY_LABELS: Record<TLeadFactCategory, string> = {
  contact: 'Contact',
  business: 'Business',
  coverage: 'Coverage',
  decision: 'Decisions',
  next_step: 'Next steps',
  other: 'Other',
};

export function LeadFactsPanel({ leadId }: ILeadFactsPanelProps) {
  const facts = useQuery(api.leadFacts.listByLead, { leadId });

  if (facts === undefined) {
    return (
      <Card aria-busy="true">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lightbulb className="h-4 w-4 text-primary" aria-hidden="true" />
            AI facts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <span className="sr-only" role="status">
            Loading AI facts
          </span>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  if (facts.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Lightbulb className="h-4 w-4 text-primary" aria-hidden="true" />
            AI facts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="No AI facts yet"
            description="Facts extracted from this lead's emails will appear here."
          />
        </CardContent>
      </Card>
    );
  }

  // Group facts by category, fixed display order, empty groups omitted below.
  const grouped: Record<TLeadFactCategory, TLeadFact[]> = {
    contact: [],
    business: [],
    coverage: [],
    decision: [],
    next_step: [],
    other: [],
  };
  for (const fact of facts) grouped[fact.category].push(fact);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="h-4 w-4 text-primary" aria-hidden="true" />
          AI facts
          <Badge variant="secondary">{facts.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {CATEGORY_ORDER.filter(category => grouped[category].length > 0).map(category => (
          <div key={category}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {CATEGORY_LABELS[category]}
            </h3>
            <ul className="space-y-2">
              {grouped[category].map(fact => (
                <li key={fact._id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-foreground">{fact.fact}</span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                    {formatDistanceToNow(fact.createdAt, { addSuffix: true })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
